/**
 * Schema Definitions — shared types for ExtractionSchema & ExtensionSchema
 *
 * SchemaFieldType mirrors CoreCustomFieldType values but is independent.
 */

import type { SystemEntity } from "./systemEntities";

export type SchemaFieldType =
  | "string"
  | "text"
  | "integer"
  | "float"
  | "numeric"
  | "boolean"
  | "date"
  | "time"
  | "datetime"
  | "enum"
  | "json"
  | "range"
  | "file"
  | "image"
  | "url"
  | "geospatial"
  | "currency"
  | "unit"
  | "object"
  | "array"
  /**
   * A reference to one record of a specific system entity. Always typed:
   * `referencedEntityType` names which entity (supplier, customer, …).
   * The stored value is `{ entityType, entityId }`. Readers that do not
   * understand this type can treat that value as a JSON object.
   */
  | "entity_reference"
  /**
   * A reference to one uploaded document. Always `CORE_DOCUMENT`. The stored
   * value is `{ entityType: "CORE_DOCUMENT", entityId }`, the same shape
   * workflow document sockets carry. A bare document id is accepted and
   * normalized to that object. Readers that do not understand this type can
   * treat the value as a JSON object.
   */
  | "document";

export type SchemaType = "extraction" | "sql";
export type SchemaMode = "standalone" | "extend_base";

export interface ValidationRules {
  min?: number;
  max?: number;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  /** Total significant digits. Paired with `scale` for the "numeric" field type (Postgres NUMERIC(precision, scale)). */
  precision?: number;
  /** Digits after the decimal point. Only meaningful alongside `precision` for the "numeric" field type. */
  scale?: number;
}

/**
 * Restricted field-type vocabulary for `schemaType: "sql"` schemas — every
 * value here is 1:1 mappable to a Postgres column primitive (see
 * `IngestTableService.fieldTypeToDdl`), unlike the full `SchemaFieldType`
 * vocabulary which also covers Extend AI extraction-only shapes (currency,
 * geospatial, etc). Kept intentionally minimal: only what AS/400 DDS types
 * (Alpha/Zoned/Packed/Binary) actually require.
 */
export const SQL_SCHEMA_FIELD_TYPES = [
  "string",
  "text",
  "integer",
  "float",
  "numeric",
  "boolean",
  "date",
] as const;

export type SqlSchemaFieldType = (typeof SQL_SCHEMA_FIELD_TYPES)[number];

/**
 * `targetModel` values that mark a schema definition as an *entity extension*:
 * user-defined metadata columns attached to an existing entity, provisioned as
 * a physical table in the app DB's `schema_ext` schema and keyed by that
 * entity's primary key.
 *
 * An extension schema must be `schemaType: "sql"` — its fields become real
 * columns, so it uses {@link SQL_SCHEMA_FIELD_TYPES} like any other sql schema.
 * Deliberately a list rather than a single constant: the provisioning engine and
 * the `core_schema_extensions` registry are keyed on `target_model`, so further
 * entities are added here rather than by forking the engine.
 */
export const EXTENSION_TARGET_MODELS = [
  "IM_PRODUCT_V3",
  "AP_INVOICE",
  "IM_INVENTORY_POLICY",
] as const;

export type ExtensionTargetModel = (typeof EXTENSION_TARGET_MODELS)[number];

const EXTENSION_TARGET_MODEL_SET: ReadonlySet<string> = new Set(
  EXTENSION_TARGET_MODELS,
);

/**
 * The stable identifier for one entity extension: the entity it extends plus the
 * schema defining it, e.g. `im_product_v3_8f14e45fceea467a9c1e1b2c3d4e5f60`.
 *
 * Both parts are immutable, which is what the identifier is for — it names the
 * provisioned table AND namespaces the workflow sockets, so neither a schema
 * rename nor a re-provision can strand a table or orphan a wired edge. Dashes are
 * stripped so the same string is a conventional SQL identifier.
 */
export function extensionIdentifier(
  targetModel: string,
  schemaDefinitionId: string,
): string {
  return `${targetModel.toLowerCase()}_${schemaDefinitionId.replace(/-/g, "")}`;
}

/** Whether an entity can be extended. This does not classify its schemas. */
export function isExtensionTargetModel(
  targetModel: string | null | undefined,
): targetModel is ExtensionTargetModel {
  return targetModel != null && EXTENSION_TARGET_MODEL_SET.has(targetModel);
}

/** Extraction schemas can target the same entity without becoming extensions. */
export function isEntityExtensionSchema(schema: {
  targetModel: string;
  schemaType: SchemaType;
}): schema is { targetModel: ExtensionTargetModel; schemaType: "sql" } {
  return (
    schema.schemaType === "sql" && isExtensionTargetModel(schema.targetModel)
  );
}

/**
 * Index kinds a schema field can declare for its ingest-table column.
 *  - "btree": default B-tree (equality + range/ordering)
 *  - "hash": hash (equality only)
 *  - "trigram": pg_trgm GIN (substring/ILIKE/regex search on character columns)
 *  - "gin": plain GIN (containment queries on JSONB columns)
 */
export const SCHEMA_FIELD_INDEX_TYPES = [
  "btree",
  "hash",
  "trigram",
  "gin",
] as const;

export type SchemaFieldIndexType = (typeof SCHEMA_FIELD_INDEX_TYPES)[number];

/**
 * Field types whose ingest column is a character type (VARCHAR/TEXT) — the
 * only columns a trigram index can serve. Must stay in sync with the ingest
 * DDL mapping (`IngestTableService.fieldTypeToDdl`).
 */
export const CHARACTER_SCHEMA_FIELD_TYPES: readonly SchemaFieldType[] = [
  "string",
  "text",
  "enum",
  "url",
  "file",
  "image",
];

/**
 * Field types whose ingest column is JSONB. B-tree/hash indexes over a large
 * jsonb value can exceed Postgres's max index-row size, so only a plain GIN
 * index is allowed on these. Must stay in sync with
 * `IngestTableService.fieldTypeToDdl`.
 */
export const JSONB_SCHEMA_FIELD_TYPES: readonly SchemaFieldType[] = [
  "json",
  "range",
  "object",
  "array",
  "unit",
  "geospatial",
  "entity_reference",
  "document",
];

const CHARACTER_FIELD_TYPE_SET: ReadonlySet<SchemaFieldType> = new Set(
  CHARACTER_SCHEMA_FIELD_TYPES,
);
const JSONB_FIELD_TYPE_SET: ReadonlySet<SchemaFieldType> = new Set(
  JSONB_SCHEMA_FIELD_TYPES,
);

/**
 * Index kinds a field of this type may declare. Character columns take
 * btree/hash/trigram; JSONB columns take only GIN; every other scalar takes
 * btree/hash.
 */
export function allowedIndexTypesForFieldType(
  fieldType: SchemaFieldType,
): readonly SchemaFieldIndexType[] {
  if (CHARACTER_FIELD_TYPE_SET.has(fieldType)) {
    return ["btree", "hash", "trigram"];
  }
  if (JSONB_FIELD_TYPE_SET.has(fieldType)) {
    return ["gin"];
  }
  return ["btree", "hash"];
}

/**
 * The index kinds a field's ingest column should actually have. Indexes are
 * opt-in: a field gets exactly the index kinds it explicitly declares in its
 * `indexes` config. `null`/`undefined` (unconfigured) and `[]` both mean "no
 * indexes" — there is no type-based default.
 */
export function effectiveIndexTypesForField(field: {
  fieldType: SchemaFieldType;
  indexes?: SchemaFieldIndexType[] | null;
}): readonly SchemaFieldIndexType[] {
  return field.indexes ?? [];
}

export interface ExtractionHints {
  aiPromptHint: string;
  exampleValues?: string[];
  synonyms?: string[];
}

export interface FieldUIConfig {
  widget?:
    | "text"
    | "textarea"
    | "select"
    | "date-picker"
    | "number"
    | "toggle"
    | "currency";
  placeholder?: string;
  width?: "sm" | "md" | "lg" | "full";
  section?: string;
}

export interface SchemaFieldDefinition {
  id: string;
  fieldName: string;
  fieldType: SchemaFieldType;
  label: string;
  description?: string;
  isRequired: boolean;
  isMultiple: boolean;
  sortOrder: number;
  coreEnumId?: string | null;
  enumValues?: string[] | null;
  parentFieldId?: string | null;
  children?: SchemaFieldDefinition[];
  defaultValue?: unknown;
  validationRules?: ValidationRules | null;
  extractionHints?: ExtractionHints | null;
  uiConfig?: FieldUIConfig | null;
  /**
   * Index kinds to provision on this field's ingest-table column (top-level
   * fields of `sql` schemas). Opt-in: only the kinds listed here are created.
   * `null`/`undefined` and `[]` both mean no indexes (see
   * {@link effectiveIndexTypesForField}).
   */
  indexes?: SchemaFieldIndexType[] | null;
  /**
   * The system entity an `entity_reference` field points at. Required for
   * that type and null for every other type.
   */
  referencedEntityType?: SystemEntity | null;
}

/**
 * Fields common to every schema definition, regardless of `schemaType`.
 * `mode` deliberately lives only on the extraction variant — see
 * {@link ExtractionSchemaDefinition} / {@link SqlSchemaDefinition}.
 */
export interface BaseSchemaDefinition {
  description?: string | null;
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  targetModel: string;
  baseSchemaRef?: string | null;
  activeVersionId?: string | null;
  version: number;
  isActive: boolean;
  /**
   * Optional document-classification this extraction schema is intended for.
   * Free-form string at the storage layer (no DB-level enum / FK) — write-time
   * validation in `SchemaDefinitionService` rejects unknown values, but the
   * column tolerates orphans introduced by later code-side enum changes so
   * read paths can keep working. Should be a value of the `DocumentType` enum
   * from `./DocumentTypes` when set.
   */
  documentType?: string | null;
  /**
   * Ordered list of top-level field ids that form the table's composite unique
   * key. The provisioned table always has a surrogate `id` UUID primary key;
   * these fields become a composite UNIQUE constraint (in this order). Null/empty
   * means no business key — the surrogate UUID is the only key.
   */
  keyedBy: string[] | null;
  /**
   * Additional column tuples that uniquely identify a row, beyond `keyedBy` —
   * each an ordered list of top-level field ids. User-declared or discovered
   * from row data ("unique index discovery"). Advisory metadata for lookups:
   * no physical UNIQUE constraint is provisioned from these.
   */
  uniqueIndexes: string[][] | null;
  /**
   * The subset of unique indexes the user asked to enforce as real UNIQUE
   * indexes on the stateful table's physical storage. Set only through the
   * explicit enforcement flow — never by discovery. Ingests that violate an
   * enforced index fail their frame instead of writing.
   */
  enforcedUniqueIndexes: string[][] | null;
  fields: SchemaFieldDefinition[];
}

/**
 * `schemaType: "sql"` — maps 1:1 to Postgres columns. Has no extraction
 * `mode`; the underlying column is stored null.
 */
export interface SqlSchemaDefinition extends BaseSchemaDefinition {
  schemaType: "sql";
}

/**
 * `schemaType: "extraction"` — AI/Extend-based schemas that
 * carry a `mode`: `standalone` uses only the defined fields, `extend_base`
 * merges them onto a registered base schema (`baseSchemaRef`).
 */
export interface ExtractionSchemaDefinition extends BaseSchemaDefinition {
  schemaType: "extraction";
  mode: SchemaMode;
}

/**
 * A schema definition as read from storage. Narrow on `schemaType` to access
 * the extraction-only `mode`.
 */
export type SchemaDefinition = SqlSchemaDefinition | ExtractionSchemaDefinition;

export interface SchemaDefinitionVersion {
  id: string;
  schemaDefinitionId: string;
  version: number;
  snapshot: SchemaDefinitionSnapshot;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Common fields of a published version snapshot. Mirrors {@link BaseSchemaDefinition}. */
export interface BaseSchemaDefinitionSnapshot {
  description?: string | null;
  name: string;
  slug: string;
  targetModel: string;
  baseSchemaRef?: string | null;
  /**
   * Document-type targeted by this schema version. Mirrors the
   * `documentType` field on the parent `SchemaDefinition` at the time the
   * version was published, so version restores keep the original mapping.
   * Free-form string for forward-compat (see `SchemaDefinition.documentType`).
   */
  documentType?: string | null;
  /**
   * Ordered list of top-level field ids that form the table's composite unique
   * key (a composite UNIQUE constraint on the provisioned table, alongside its
   * surrogate `id` UUID primary key). Null/empty means no business key.
   */
  keyedBy: string[] | null;
  fields: SchemaFieldDefinition[];
}

export interface SqlSchemaDefinitionSnapshot extends BaseSchemaDefinitionSnapshot {
  schemaType: "sql";
}

export interface ExtractionSchemaDefinitionSnapshot extends BaseSchemaDefinitionSnapshot {
  schemaType: "extraction";
  mode: SchemaMode;
}

export type SchemaDefinitionSnapshot =
  SqlSchemaDefinitionSnapshot | ExtractionSchemaDefinitionSnapshot;
